'use client'

import { useEffect } from 'react'

// Imports directos a los ficheros hoja, NUNCA al barrel `index.ts`: ese
// barrel también re-exporta `resolveErrorPageCode`/`resolveErrorPageOutcome`,
// que importan `ApiRequestError` de `@/app/lib/api/api` → `services.ts`. Ese
// módulo lanza en su propio scope si falta `BASE_DOMAIN` — y al ser este
// archivo `'use client'`, importar el barrel arrastra ese `throw` al bundle
// de navegador, donde `process.env.BASE_DOMAIN` (var server-only) siempre es
// `undefined`. Resultado: "Missing BASE_DOMAIN environment variable" en
// TODOS los entornos, no solo en local — el módulo entero se evalúa al
// importar, aunque solo se use un export sin relación con `api.ts`.
import { ErrorPageFallbackShell } from '@/app/components/shared/ErrorPageFallback/ErrorPageFallbackShell'
import { GENERIC_SERVER_ERROR_CODE, TITLE } from '@/app/components/shared/ErrorPageFallback/ErrorPageFallback.constants'
import { parseErrorPageDigest } from '@/app/components/shared/ErrorPageFallback/errorPageDigest'

/**
 * Página de error de `(general-web)` que NUNCA se cachea: la pintan los
 * errores que los `try/catch` de cada `page.tsx`/`*Content.tsx` relanzan a
 * propósito (`UncachedPageError` de `resolveErrorPageOutcome`: 5xx, 4xx que
 * no son 404, timeout/red) y, como red de seguridad, cualquier otra excepción
 * no capturada. Al lanzar el render, ISR no guarda nada: conserva la última
 * versión buena si la hay, y si no responde con este 500 sin caché.
 *
 * Un error boundary de Next.js **debe** ser Client Component
 * (`node_modules/next/dist/docs/.../error.md`), así que no recibe las
 * propiedades custom del error de servidor (`ApiRequestError.status`) — solo
 * `message`/`digest`, y en producción ni siquiera el mensaje real (se
 * sanea). El código real viaja en el `digest` (`parseErrorPageDigest`);
 * sin él (error no previsto) se muestra el `500` genérico. Reutiliza
 * `ErrorPageFallbackShell` (sin `MODULES_MAP`) en vez de `ErrorPageFallback`
 * — ver el comentario de ese componente.
 *
 * Al vivir en `(general-web)`, Next.js NO lo usa para errores del propio
 * `layout.tsx` de ese mismo segmento (suben al límite del segmento padre) —
 * cumple sin lógica adicional la excepción "salvo en el layout" del
 * requisito de este cambio.
 */
export default function GeneralWebError({
  error,
}: {
  readonly error: Error & { digest?: string }
  readonly reset: () => void
}) {
  useEffect(() => {
    console.error(error)
  }, [error])

  const code = parseErrorPageDigest(error.digest) ?? GENERIC_SERVER_ERROR_CODE

  return <ErrorPageFallbackShell title={TITLE} code={code} />
}
